'use client';

import {
  createContext,
  useState,
  useEffect,
  useContext,
  useId,
  useRef,
  useCallback,
} from 'react';

export type PendingChangesDiscardHandler = (
  continueNavigation: () => void,
) => void;

/** Kill switch for the whole unsaved-changes guard. */
const PENDING_CHANGES_OFF =
  process.env.NEXT_PUBLIC_NOMI_PENDING_CHANGES_OFF === '1';

interface PendingChangesContextProps {
  hasPendingChanges: boolean;
  setHasPendingChanges: (value: boolean) => void;
  /**
   * Keyed alternative to `setHasPendingChanges` for surfaces that own a form, so
   * several mounted at once (an interception modal over a page form) compose
   * instead of overwriting each other. `false` retracts only that surface.
   * Prefer `usePendingChangesClaim` over calling this directly.
   */
  setSurfacePendingChanges: (id: string, value: boolean) => void;
  registerDiscardHandler: (
    handler: PendingChangesDiscardHandler | null,
  ) => void;
  invokeDiscardHandler: (continueNavigation: () => void) => boolean;
}

type PendingChangesProviderProps = {
  children: React.ReactNode;
};

export const PendingChangesContext = createContext<
  PendingChangesContextProps | undefined
>(undefined);

// Module-level: consumers put these callbacks in effect dependency lists, so a
// fresh object per render would re-run those effects on every render.
const NOOP_CONTEXT: PendingChangesContextProps = {
  hasPendingChanges: false,
  setHasPendingChanges: () => {},
  setSurfacePendingChanges: () => {},
  registerDiscardHandler: () => {},
  invokeDiscardHandler: () => false,
};

export const usePendingChanges = (): PendingChangesContextProps =>
  useContext(PendingChangesContext) ?? NOOP_CONTEXT;

/**
 * Claims unsaved changes for one surface, for as long as the caller is mounted.
 *
 * Always prefer this to `setHasPendingChanges`. Claims are keyed, so surfaces
 * mounted together compose rather than overwrite, the claim is retracted on
 * unmount (the provider outlives every route, so a stale `true` would prompt on
 * the next form the user opens), and nothing else can drop it —
 * `useServerAction` clears the imperative flag after *every* server action,
 * which otherwise wipes a still-dirty editor's state permanently.
 */
export const usePendingChangesClaim = (hasChanges: boolean) => {
  const surfaceId = useId();
  const { setSurfacePendingChanges } = usePendingChanges();

  useEffect(() => {
    setSurfacePendingChanges(surfaceId, hasChanges);
    return () => setSurfacePendingChanges(surfaceId, false);
  }, [surfaceId, hasChanges, setSurfacePendingChanges]);
};

const PendingChangesContextProvider: React.FC<PendingChangesProviderProps> = ({
  children,
}) => {
  const [manualPendingChanges, setManualPendingChanges] =
    useState<boolean>(false);
  const [dirtySurfaces, setDirtySurfaces] = useState<ReadonlySet<string>>(
    () => new Set(),
  );
  const discardHandlerRef = useRef<PendingChangesDiscardHandler | null>(null);

  const setSurfacePendingChanges = useCallback((id: string, value: boolean) => {
    setDirtySurfaces((prev) => {
      if (prev.has(id) === value) return prev;

      const next = new Set(prev);
      if (value) {
        next.add(id);
      } else {
        next.delete(id);
      }
      return next;
    });
  }, []);

  const registerDiscardHandler = useCallback(
    (handler: PendingChangesDiscardHandler | null) => {
      discardHandlerRef.current = handler;
    },
    [],
  );

  const invokeDiscardHandler = useCallback((continueNavigation: () => void) => {
    const handler = discardHandlerRef.current;
    if (!handler) {
      return false;
    }

    handler(continueNavigation);
    return true;
  }, []);

  // Two independent sources so imperative callers cannot clear a surface's
  // claim, which is what stops a stale `true` leaking across routes.
  const hasPendingChanges =
    !PENDING_CHANGES_OFF && (manualPendingChanges || dirtySurfaces.size > 0);

  return (
    <PendingChangesContext.Provider
      value={{
        hasPendingChanges,
        // Memoised for the same reason as NOOP_CONTEXT above.
        setHasPendingChanges: setManualPendingChanges,
        setSurfacePendingChanges,
        registerDiscardHandler,
        invokeDiscardHandler,
      }}
    >
      {children}
    </PendingChangesContext.Provider>
  );
};

export default PendingChangesContextProvider;
